import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  SelectChangeEvent,
  type SelectProps,
} from '@mui/material';
import React, { useCallback } from 'react';

type DropdownProps = SelectProps & {
  label?: string;
  name?: string;
  value?: boolean | string | number;
  error?: boolean;
  sx?: Record<string, unknown>;
  helperText?: string;
  placeholder?: string;
  fullWidth?: boolean;
  variant?: 'filled' | 'outlined' | 'standard';
  renderSelectedValue?: (value: string | number | boolean) => string;
  onChange?: (event: SelectChangeEvent<string | number | boolean>) => void;
  options:
    | Array<{
        value?: string | number | boolean;
        label?: string;
        code?: string;
        title?: string;
      }>
    | undefined;
};

const selectMenuProps = { PaperProps: { style: { maxHeight: 500 } } };

const sxLabel = { ml: -1.5 };

const sxHelperText = { marginLeft: 0 };

export default function Dropdown({
  sx,
  name,
  label,
  value,
  error,
  options,
  onChange,
  disabled,
  readOnly,
  helperText,
  fullWidth = true,
  renderSelectedValue,
  variant = 'standard',
  placeholder,
}: DropdownProps) {
  if (options == null) return null;

  const handleChange = (
    event: SelectChangeEvent<string | number | boolean>,
  ) => {
    if (onChange) {
      // If the original value is boolean, convert the string back to boolean before passing up
      if (typeof value === 'boolean') {
        // Create a new event with boolean value
        const newEvent = {
          ...event,
          target: {
            ...event.target,
            value: event.target.value === 'true',
          },
        } as SelectChangeEvent<boolean>;
        onChange(
          newEvent as unknown as SelectChangeEvent<string | number | boolean>,
        );
      } else {
        onChange(event);
      }
    }
  };

  const renderValue = useCallback(
    (selectedValue: unknown) => {
      // If no selected value, show the placeholder
      if ((selectedValue == null || selectedValue === '') && label) {
        return '';
      }

      // If renderSelectedValue is passed as a function, use it
      if (renderSelectedValue) {
        return renderSelectedValue(selectedValue as string | number | boolean);
      }

      // Default behavior: Find the selected option and return the label
      const selectedOption = options.find((option) => {
        const optVal =
          typeof option.value === 'boolean'
            ? String(option.value)
            : option.value;
        const selVal =
          typeof selectedValue === 'boolean'
            ? String(selectedValue)
            : selectedValue;

        return optVal === selVal || option.code === selectedValue;
      });

      return selectedOption ? selectedOption.label || selectedOption.title : '';
    },
    [label, options, renderSelectedValue],
  );

  return (
    <FormControl fullWidth={fullWidth} error={error}>
      {placeholder && <InputLabel sx={sxLabel}>{placeholder}</InputLabel>}
      {label && <InputLabel variant="standard">{label}</InputLabel>}
      <Select
        name={name}
        // value={typeof value === 'boolean' ? String(value) : value}
        value={value}
        onChange={handleChange}
        MenuProps={selectMenuProps}
        variant={variant}
        displayEmpty
        sx={sx}
        readOnly={readOnly}
        disabled={disabled}
        renderValue={renderValue}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}

        {options.map((option) => {
          const { value, label, title, code } = option;
          // Convert boolean values to strings for display in menu items
          const displayValue =
            typeof value === 'boolean'
              ? String(value)
              : value !== undefined
                ? value
                : code;
          // Use the original value for internal tracking
          const actualValue = value !== undefined ? value : code;

          return (
            <MenuItem key={actualValue?.toString()} value={displayValue}>
              {label || title}
            </MenuItem>
          );
        })}
      </Select>
      {helperText && (
        <FormHelperText sx={sxHelperText}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}
